(() => {
  if (window.AlcimoSearchOverlayLoaded) return;
  window.AlcimoSearchOverlayLoaded = true;

  const SEARCH_PATH = (window.routes && window.routes.search_url) || '/search';
  let overlay;
  let input;
  let results;
  let status;
  let controller;
  let debounceTimer;
  let allProducts = [];
  let lastFocusedElement = null;

  const escapeHTML = (value = '') => {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  };

  const normalizePrice = (value) => {
    if (typeof value === 'string') {
      const normalized = Number(value.replace(',', '.'));
      return Number.isFinite(normalized) ? normalized : 0;
    }

    const numeric = Number(value) || 0;

    // Alguns endpoints retornam centavos; outros retornam reais.
    return Number.isInteger(numeric) && numeric >= 10000 ? numeric / 100 : numeric;
  };

  const formatMoney = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(normalizePrice(value));

  const getHeader = () =>
    document.querySelector(
      '.alcimo-header, .alcimo-smart-header, header[id^="AlcimoHeader"], .shopify-section-group-header-group header'
    ) || document.querySelector('header');


  const syncOverlayBrand = () => {
    if (!overlay) return;

    const brand = overlay.querySelector('[data-alcimo-overlay-brand]');
    const header = getHeader();
    const sourceLogo =
      header?.querySelector(
        '.alcimo-header__logo img, .alcimo-smart-header__logo img, a[class*="header"][class*="logo"] img, .header__heading-logo'
      ) || null;

    const sourceText =
      header?.querySelector(
        '.alcimo-header__logo span, .alcimo-smart-header__logo span, .header__heading-link span'
      ) || null;

    if (!brand) return;

    brand.innerHTML = '';

    if (sourceLogo) {
      const image = sourceLogo.cloneNode(true);
      image.removeAttribute('id');
      image.removeAttribute('loading');
      image.setAttribute('loading', 'eager');
      image.setAttribute('data-alcimo-overlay-logo', '');
      brand.appendChild(image);
      return;
    }

    const fallback = document.createElement('span');
    fallback.textContent = sourceText?.textContent?.trim() || 'ALCIMO & CO.';
    brand.appendChild(fallback);
  };

  const getProductPrice = (product) =>
    product.price ??
    product.price_min ??
    product.variants?.[0]?.price ??
    0;

  const getProductImage = (product) =>
    product.featured_image ??
    product.image ??
    product.images?.[0]?.src ??
    product.images?.[0] ??
    '';

  const getProductUrl = (product) =>
    product.url ??
    (product.handle ? `/products/${product.handle}` : '#');

  const normalizeProduct = (product) => ({
    title: product.title || '',
    url: getProductUrl(product),
    image: getProductImage(product),
    price: getProductPrice(product),
    type: product.product_type || product.type || '',
  });

  const createOverlay = () => {
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.className = 'alcimo-search-overlay';
    overlay.setAttribute('data-alcimo-search-overlay', '');
    overlay.setAttribute('aria-hidden', 'true');

    overlay.innerHTML = `
      <div class="alcimo-search-overlay__panel" role="dialog" aria-modal="true" aria-label="Pesquisar produtos">
        <div class="alcimo-search-overlay__header">
          <div class="alcimo-search-overlay__brand" data-alcimo-overlay-brand>
            <span>ALCIMO &amp; CO.</span>
          </div>

          <button type="button" class="alcimo-search-overlay__close" aria-label="Fechar pesquisa">
            <span></span><span></span>
          </button>
        </div>

        <div class="alcimo-search-overlay__content">
          <form action="${SEARCH_PATH}" method="get" role="search" class="alcimo-search-overlay__form">
            <input type="hidden" name="type" value="product">
            <input type="hidden" name="options[prefix]" value="last">

            <div class="alcimo-search-overlay__field">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="6.6"></circle>
                <path d="M16 16l4.3 4.3"></path>
              </svg>

              <label class="visually-hidden" for="AlcimoOverlaySearchInput">Pesquisar produtos</label>

              <input
                id="AlcimoOverlaySearchInput"
                type="search"
                name="q"
                placeholder="O que você procura?"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
              >
            </div>
          </form>

          <div class="alcimo-search-overlay__status" aria-live="polite"></div>

          <div class="alcimo-search-overlay__body">
            <aside class="alcimo-search-overlay__aside">
              <p class="alcimo-search-overlay__eyebrow">Explore</p>
              <button type="button" data-alcimo-filter="all" class="is-active">Todos os produtos</button>
              <div data-alcimo-type-links></div>
            </aside>

            <section class="alcimo-search-overlay__catalog">
              <div class="alcimo-search-overlay__catalog-head">
                <div>
                  <p class="alcimo-search-overlay__eyebrow">Disponíveis agora</p>
                  <h2 data-alcimo-catalog-title>Todos os produtos</h2>
                </div>

                <a href="/collections/all" class="alcimo-search-overlay__all-results">
                  Ver todos os produtos →
                </a>
              </div>

              <div class="alcimo-search-overlay__results"></div>
            </section>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    syncOverlayBrand();

    input = overlay.querySelector('input[type="search"]');
    results = overlay.querySelector('.alcimo-search-overlay__results');
    status = overlay.querySelector('.alcimo-search-overlay__status');

    overlay.querySelector('.alcimo-search-overlay__close').addEventListener('click', closeOverlay);
    input.addEventListener('input', onSearchInput);

    overlay.addEventListener('click', (event) => {
      const filter = event.target.closest('[data-alcimo-filter]');
      if (!filter) return;

      overlay.querySelectorAll('[data-alcimo-filter]').forEach((button) => {
        button.classList.toggle('is-active', button === filter);
      });

      const type = filter.dataset.alcimoFilter;
      const title = overlay.querySelector('[data-alcimo-catalog-title]');
      title.textContent = type === 'all' ? 'Todos os produtos' : type;

      const filtered =
        type === 'all'
          ? allProducts
          : allProducts.filter((product) => product.type === type);

      renderProducts(filtered);
    });

    injectStyles();
    return overlay;
  };

  const openOverlay = async () => {
    createOverlay();
    syncOverlayBrand();
    lastFocusedElement = document.activeElement;

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('alcimo-search-overlay-open');

    window.setTimeout(() => input.focus(), 350);

    if (!allProducts.length) {
      await loadAllProducts();
    } else {
      renderProducts(allProducts);
    }
  };

  const closeOverlay = () => {
    if (!overlay) return;

    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('alcimo-search-overlay-open');

    if (controller) controller.abort();
    lastFocusedElement?.focus?.();
  };

  const loadAllProducts = async () => {
    status.textContent = 'Carregando produtos disponíveis...';

    try {
      let response = await fetch('/collections/all/products.json?limit=50', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) {
        response = await fetch('/products.json?limit=50', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      allProducts = (data.products || []).map(normalizeProduct);

      buildTypeLinks();
      status.textContent =
        allProducts.length === 1
          ? '1 produto disponível'
          : `${allProducts.length} produtos disponíveis`;

      renderProducts(allProducts);
    } catch (error) {
      status.textContent = '';
      renderEmpty('Não foi possível carregar os produtos agora.');
      console.error('ALCIMO: erro ao carregar catálogo.', error);
    }
  };

  const buildTypeLinks = () => {
    const container = overlay.querySelector('[data-alcimo-type-links]');
    const types = [...new Set(allProducts.map((product) => product.type).filter(Boolean))];

    container.innerHTML = types
      .map(
        (type) =>
          `<button type="button" data-alcimo-filter="${escapeHTML(type)}">${escapeHTML(type)}</button>`
      )
      .join('');
  };

  const renderProducts = (products) => {
    if (!products.length) {
      renderEmpty('Nenhum produto encontrado.');
      return;
    }

    results.innerHTML = products
      .slice(0, 12)
      .map(
        (product, index) => `
          <a
            class="alcimo-search-overlay__product"
            href="${product.url}"
            style="animation-delay:${index * 35}ms"
          >
            <div class="alcimo-search-overlay__media">
              ${
                product.image
                  ? `<img src="${product.image}" alt="${escapeHTML(product.title)}" loading="lazy">`
                  : ''
              }
            </div>
            <h3>${escapeHTML(product.title)}</h3>
            <p>${formatMoney(product.price)}</p>
          </a>
        `
      )
      .join('');
  };

  const renderEmpty = (message) => {
    results.innerHTML = `
      <div class="alcimo-search-overlay__empty" role="status">
        <strong>${escapeHTML(message)}</strong>
        <p>No momento, esse item não está disponível ou não faz parte do catálogo da ALCIMO &amp; CO.</p>
      </div>
    `;
  };

  const searchProducts = async () => {
    const query = input.value.trim();

    if (!query) {
      status.textContent =
        allProducts.length === 1
          ? '1 produto disponível'
          : `${allProducts.length} produtos disponíveis`;
      overlay.querySelector('[data-alcimo-catalog-title]').textContent = 'Todos os produtos';
      renderProducts(allProducts);
      return;
    }

    if (query.length < 2) {
      status.textContent = 'Digite pelo menos 2 letras.';
      return;
    }

    status.textContent = 'Buscando produtos...';

    if (controller) controller.abort();
    controller = new AbortController();

    try {
      const url =
        `/search/suggest.json?q=${encodeURIComponent(query)}` +
        `&resources[type]=product` +
        `&resources[limit]=12` +
        `&resources[options][unavailable_products]=last`;

      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const products = (data?.resources?.results?.products || []).map(normalizeProduct);

      overlay.querySelector('[data-alcimo-catalog-title]').textContent = `Resultados para “${query}”`;

      if (!products.length) {
        status.textContent = 'Nenhum produto encontrado';
        renderEmpty(`Não encontramos “${query}”.`);
        return;
      }

      status.textContent =
        products.length === 1
          ? '1 produto encontrado'
          : `${products.length} produtos encontrados`;

      renderProducts(products);
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.textContent = '';
      renderEmpty(`Não encontramos “${query}”.`);
      console.error('ALCIMO: erro na pesquisa.', error);
    }
  };

  const onSearchInput = () => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(searchProducts, 250);
  };

  const isSearchLink = (element) => {
    const href = element?.getAttribute?.('href');
    if (!href) return false;

    try {
      const url = new URL(href, window.location.origin);
      return url.pathname === '/search' || url.pathname === SEARCH_PATH;
    } catch {
      return href === '/search' || href === SEARCH_PATH;
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('a, button');

      if (
        trigger?.matches('[data-alcimo-search-toggle], [data-alcimo-search-open]') ||
        (trigger?.closest('header') && isSearchLink(trigger))
      ) {
        event.preventDefault();
        event.stopPropagation();
        openOverlay();
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && overlay?.classList.contains('is-open')) {
      closeOverlay();
    }
  });

  const injectStyles = () => {
    if (document.getElementById('alcimo-search-overlay-styles')) return;

    const style = document.createElement('style');
    style.id = 'alcimo-search-overlay-styles';
    style.textContent = `
      body.alcimo-search-overlay-open {
        overflow: hidden;
      }

      .alcimo-search-overlay {
        position: fixed;
        inset: 0;
        z-index: 99999;
        pointer-events: none;
        background: rgba(0,0,0,.16);
        opacity: 0;
        visibility: hidden;
        transition: opacity .3s ease, visibility 0s linear .45s;
      }

      .alcimo-search-overlay.is-open {
        pointer-events: auto;
        opacity: 1;
        visibility: visible;
        transition: opacity .3s ease;
      }

      .alcimo-search-overlay__panel {
        width: 100%;
        max-height: 94vh;
        overflow-y: auto;
        background: #f7f4ee;
        color: #171717;
        transform: translateY(-102%);
        transition: transform .62s cubic-bezier(.22,.61,.36,1);
        box-shadow: 0 24px 70px rgba(0,0,0,.18);
      }

      .alcimo-search-overlay.is-open .alcimo-search-overlay__panel {
        transform: translateY(0);
      }

      .alcimo-search-overlay__header {
        min-height: 112px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        padding: 0 42px;
        border-bottom: 1px solid rgba(23,23,23,.10);
      }

      .alcimo-search-overlay__brand {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 42px;
        font-family: var(--font-heading-family);
        font-size: 24px;
        letter-spacing: .02em;
      }

      .alcimo-search-overlay__brand img {
        display: block;
        width: auto;
        max-width: 220px;
        max-height: 42px;
        height: auto;
        object-fit: contain;
        filter: brightness(0);
      }

      .alcimo-search-overlay__close {
        position: absolute;
        top: 50%;
        right: 42px;
        width: 36px;
        height: 36px;
        padding: 0;
        border: 0;
        background: #171717;
        border-radius: 50%;
        transform: translateY(-50%);
        cursor: pointer;
      }

      .alcimo-search-overlay__close span {
        position: absolute;
        top: 50%;
        left: 9px;
        width: 18px;
        height: 1px;
        background: #fff;
      }

      .alcimo-search-overlay__close span:first-child { transform: rotate(45deg); }
      .alcimo-search-overlay__close span:last-child { transform: rotate(-45deg); }

      .alcimo-search-overlay__content {
        width: min(100% - 64px, 1440px);
        margin: 0 auto;
        padding: 28px 0 44px;
      }

      .alcimo-search-overlay__field {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: center;
        gap: 14px;
        border-bottom: 1px solid rgba(23,23,23,.30);
      }

      .alcimo-search-overlay__field svg {
        width: 20px;
        height: 20px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.4;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .alcimo-search-overlay__field input {
        width: 100%;
        min-height: 62px;
        padding: 6px 0;
        border: 0;
        background: transparent;
        color: inherit;
        font-family: var(--font-heading-family);
        font-size: 24px;
        font-weight: 400;
        outline: none;
        box-shadow: none;
      }

      .alcimo-search-overlay__status {
        min-height: 18px;
        margin-top: 12px;
        color: #756f68;
        font-size: 11px;
      }

      .alcimo-search-overlay__body {
        display: grid;
        grid-template-columns: 220px minmax(0,1fr);
        gap: 54px;
        margin-top: 20px;
      }

      .alcimo-search-overlay__aside {
        padding-top: 10px;
      }

      .alcimo-search-overlay__eyebrow {
        margin: 0 0 18px;
        color: #8b8178;
        font-size: 9px;
        font-weight: 600;
        letter-spacing: .18em;
        text-transform: uppercase;
      }

      .alcimo-search-overlay__aside button {
        width: 100%;
        padding: 8px 0;
        border: 0;
        background: transparent;
        color: #171717;
        font: inherit;
        font-size: 14px;
        text-align: left;
        cursor: pointer;
        opacity: .72;
      }

      .alcimo-search-overlay__aside button.is-active,
      .alcimo-search-overlay__aside button:hover {
        opacity: 1;
        text-decoration: underline;
        text-underline-offset: 4px;
      }

      .alcimo-search-overlay__catalog-head {
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 24px;
        margin-bottom: 20px;
      }

      .alcimo-search-overlay__catalog-head h2 {
        margin: 0;
        font-family: var(--font-heading-family);
        font-size: 28px;
        font-weight: 400;
      }

      .alcimo-search-overlay__all-results {
        color: inherit;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: .14em;
        text-decoration: none;
        text-transform: uppercase;
        white-space: nowrap;
      }

      .alcimo-search-overlay__results {
        display: grid;
        grid-template-columns: repeat(4,minmax(0,1fr));
        gap: 26px 18px;
      }

      .alcimo-search-overlay__product {
        color: inherit;
        text-decoration: none;
        opacity: 0;
        transform: translateY(10px);
        animation: alcimoOverlayProductIn .34s ease forwards;
      }

      .alcimo-search-overlay__media {
        aspect-ratio: 4/5;
        overflow: hidden;
        background: #eeeae3;
      }

      .alcimo-search-overlay__media img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
        transition: transform .38s ease;
      }

      .alcimo-search-overlay__product:hover img {
        transform: scale(1.025);
      }

      .alcimo-search-overlay__product h3 {
        margin: 10px 0 5px;
        font-family: var(--font-heading-family);
        font-size: 15px;
        font-weight: 400;
      }

      .alcimo-search-overlay__product p {
        margin: 0;
        color: #756f68;
        font-size: 12px;
      }

      .alcimo-search-overlay__empty {
        grid-column: 1/-1;
        padding: 28px 0;
        border-top: 1px solid rgba(23,23,23,.12);
      }

      .alcimo-search-overlay__empty strong {
        display: block;
        margin-bottom: 8px;
        font-family: var(--font-heading-family);
        font-size: 24px;
        font-weight: 400;
      }

      .alcimo-search-overlay__empty p {
        max-width: 640px;
        margin: 0;
        color: #756f68;
        font-size: 13px;
        line-height: 1.7;
      }

      @keyframes alcimoOverlayProductIn {
        to { opacity: 1; transform: translateY(0); }
      }

      @media (max-width: 989px) {
        .alcimo-search-overlay__body {
          grid-template-columns: 1fr;
          gap: 24px;
        }

        .alcimo-search-overlay__aside {
          display: flex;
          gap: 14px;
          overflow-x: auto;
          padding: 0 0 6px;
        }

        .alcimo-search-overlay__aside .alcimo-search-overlay__eyebrow {
          display: none;
        }

        .alcimo-search-overlay__aside > button,
        .alcimo-search-overlay__aside > div {
          display: contents;
        }

        .alcimo-search-overlay__aside button {
          width: auto;
          white-space: nowrap;
        }

        .alcimo-search-overlay__results {
          grid-template-columns: repeat(3,minmax(0,1fr));
        }
      }

      @media (max-width: 749px) {
        .alcimo-search-overlay__header {
          min-height: 84px;
          padding: 0 20px;
        }

        .alcimo-search-overlay__brand {
          min-height: 34px;
          font-size: 19px;
        }

        .alcimo-search-overlay__brand img {
          max-width: 165px;
          max-height: 34px;
        }

        .alcimo-search-overlay__close {
          right: 20px;
        }

        .alcimo-search-overlay__content {
          width: calc(100% - 36px);
          padding: 20px 0 32px;
        }

        .alcimo-search-overlay__field input {
          min-height: 56px;
          font-size: 21px;
        }

        .alcimo-search-overlay__catalog-head {
          align-items: start;
          flex-direction: column;
          gap: 12px;
        }

        .alcimo-search-overlay__results {
          grid-template-columns: repeat(2,minmax(0,1fr));
          gap: 20px 12px;
        }
      }
    `;

    document.head.appendChild(style);
  };

  document.addEventListener('shopify:section:load', () => {
    window.setTimeout(syncOverlayBrand, 50);
  });

  document.addEventListener('shopify:section:select', () => {
    window.setTimeout(syncOverlayBrand, 50);
  });

  injectStyles();
})();